import { createContext, useContext, useEffect, useState } from 'react';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useAppContainerContext } from './AppContainerContext';
import { useChatMessages } from './chat/hooks/useChatMessages';
import { ChatMode, ChatState, Message, StoredMessage } from './chat/types';
import { PreviousChatContext } from './sidebar/DisplacedChatBanner';
import { useDisplacedChat } from './sidebar/useDisplacedChat';
import { useAppContainer } from './useAppContainer';

type SiestaContextValue = {
  messages: Message[];
  latestIncomingId: string | null;
  currentChatState: ChatState | null;
  previousChatContext: PreviousChatContext | null;
  usedActionIds: Set<string>;
  sendUserMessage: (
    content: string,
    clientId: string | null,
    conversationId: string,
  ) => Promise<void>;
  clearMessages: () => void;
  loadHistory: (rawMessages: StoredMessage[]) => void;
  replaceLastAssistantMessage: (content: string) => void;
  appendInterruptedMarker: () => void;
  finaliseLimitReachedMessage: (id: string) => void;
  cancelStreaming: () => void;
  resetIfDisplaced: () => string;
  discardChat: () => string;
  goBack: () => void;
  markActionUsed: (id: string) => void;
  clearUsedActionIds: () => void;
};

const SiestaContext = createContext<SiestaContextValue | undefined>(undefined);

export function SiestaProvider({ children }: { children: React.ReactNode }) {
  const { contentMeta } = useAppContainerContext();
  const { siestaMode } = useAppContainer();
  const { clientId } = useCurrentClientIfAvailable();

  const isDocumentMode = contentMeta?.chatMode === ChatMode.DOCUMENT;
  const requestBodyBuilder = (message: string, activeConversationId: string) => ({
    conversationId: activeConversationId,
    documentContent:
      contentMeta?.getDocumentContent?.() ?? contentMeta?.metadata?.documentContent ?? '',
    editInstruction: message,
    clientId,
  });
  const chatOptions = isDocumentMode
    ? {
        mode: ChatMode.DOCUMENT as typeof ChatMode.DOCUMENT,
        entityId: contentMeta?.entityId ?? '',
        onStateChange: contentMeta?.onChatStateChange,
        requestBodyBuilder,
      }
    : {
        mode: ChatMode.CLIENT as typeof ChatMode.CLIENT,
        onStateChange: contentMeta?.onChatStateChange,
      };
  const chat = useChatMessages(chatOptions);

  const [usedActionIds, setUsedActionIds] = useState<Set<string>>(new Set());
  const markActionUsed = (id: string) => setUsedActionIds(prev => new Set(prev).add(id));
  const clearUsedActionIds = () => setUsedActionIds(new Set());

  const tracking = useDisplacedChat({
    messageCount: chat.messages.length,
    clientId,
    clearMessages: chat.clearMessages,
    clearUsedActionIds,
    cancelStreaming: chat.cancelStreaming,
  });

  useEffect(() => {
    if (siestaMode !== 'chat' && siestaMode !== 'busy') {
      chat.cancelStreaming();
    }
  }, [siestaMode, chat.cancelStreaming]);

  const chatActionsRef = contentMeta?.chatActionsRef;
  useEffect(() => {
    if (chatActionsRef) {
      chatActionsRef.current = { replaceLastAssistantMessage: chat.replaceLastAssistantMessage };
    }
    return () => {
      if (chatActionsRef) chatActionsRef.current = null;
    };
  }, [chatActionsRef, chat.replaceLastAssistantMessage]);

  return (
    <SiestaContext
      value={{ ...chat, ...tracking, usedActionIds, markActionUsed, clearUsedActionIds }}
    >
      {children}
    </SiestaContext>
  );
}

export function useSiesta() {
  const ctx = useContext(SiestaContext);
  if (ctx === undefined) {
    throw new Error('useSiesta must be used within a SiestaProvider');
  }
  return ctx;
}
